import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { htmlSafe } from '@ember/template';
import { arcPath, arcMidAngle, polarToCartesian } from '../../utils/charts/svg-utils.ts';
import { DEFAULT_CHART_COLORS } from '../../utils/charts/color-palette.ts';
import { formatNumber } from '../../utils/charts/chart-layout.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';
import type { ChartDataPoint, ChartOptions } from '../../types/charts.ts';

// ---------------------------------------------------------------------------
// DonutChart – pure SVG donut/ring chart with interactive arcs & legend
// ---------------------------------------------------------------------------

interface DonutChartSignature {
  Args: ChartOptions & {
    /** Chart data points */
    data: ChartDataPoint[];
    /** Ring thickness in pixels (default: 60) */
    ringThickness?: number;
    /** Optional max size in pixels — caps the chart when the container is wide (default: no cap) */
    maxSize?: number;
    /** Pin the chart to an exact pixel size, bypassing ResizeObserver entirely */
    size?: number;
  };
  Element: HTMLDivElement;
}

interface ArcSegment {
  path: string;
  color: string;
  data: ChartDataPoint;
  startAngle: number;
  endAngle: number;
  percent: number;
  labelX: number;
  labelY: number;
}

const RING_THICKNESS = 60;
const MIN_SIZE = 160;

export default class DonutChart extends Component<DonutChartSignature> {
  @tracked containerWidth = 300;
  @tracked containerHeight = 300;
  @tracked hoveredIndex: number | null = null;

  get colors() {
    return this.args.colors ?? DEFAULT_CHART_COLORS;
  }

  get total() {
    return this.args.data.reduce((sum, d) => sum + d.value, 0) || 1;
  }

  get size() {
    // If an explicit size is provided, use it directly — no ResizeObserver needed
    if (this.args.size) return this.args.size;
    const usable = Math.min(this.containerWidth, this.containerHeight);
    const capped = this.args.maxSize ? Math.min(usable, this.args.maxSize) : usable;
    return Math.max(MIN_SIZE, capped);
  }

  get ringThickness() {
    return this.args.ringThickness ?? RING_THICKNESS;
  }

  get outerRadius() {
    return (this.size - 4) / 2;
  }

  get innerRadius() {
    return Math.max(0, this.outerRadius - this.ringThickness);
  }

  get cx() {
    return this.size / 2;
  }

  get cy() {
    return this.size / 2;
  }

  get viewBox() {
    return `0 0 ${this.size} ${this.size}`;
  }

  get segments(): ArcSegment[] {
    const { data } = this.args;
    if (!data.length) return [];

    let currentAngle = 0;
    const labelRadius = this.innerRadius + (this.outerRadius - this.innerRadius) / 2;

    return data.map((d, i) => {
      const fraction = d.value / this.total;
      const startAngle = currentAngle;
      const endAngle = currentAngle + fraction * Math.PI * 2 - 0.005;
      currentAngle += fraction * Math.PI * 2;

      const mid = arcMidAngle(startAngle, endAngle);
      const labelPos = polarToCartesian(this.cx, this.cy, labelRadius, mid);

      return {
        path: arcPath({
          cx: this.cx,
          cy: this.cy,
          outerRadius: this.outerRadius,
          innerRadius: this.innerRadius,
          startAngle,
          endAngle: Math.max(endAngle, startAngle + 0.01),
        }),
        color: this.colors[i % this.colors.length]!,
        data: d,
        startAngle,
        endAngle,
        percent: Math.round(fraction * 100),
        labelX: labelPos.x,
        labelY: labelPos.y,
      };
    });
  }

  get showLegend() {
    return this.args.showLegend !== false;
  }

  get legendPosition() {
    return this.args.legendPosition ?? 'bottom';
  }

  get isSideLegend() {
    return this.legendPosition === 'left' || this.legendPosition === 'right';
  }

  // Flex direction places the legend on the requested side; default 'bottom'
  // reproduces the original column layout.
  get rootClass() {
    switch (this.legendPosition) {
      case 'top':
        return 'flex flex-col-reverse items-center justify-center gap-4';
      case 'left':
        return 'flex flex-row-reverse items-center justify-center gap-4';
      case 'right':
        return 'flex flex-row items-center justify-center gap-4';
      default:
        return 'flex flex-col items-center justify-center gap-4';
    }
  }

  get chartWrapperClass() {
    const base = 'tio-flex-center relative flex-1 min-h-0';
    return this.isSideLegend ? base : `${base} w-full`;
  }

  get legendClass() {
    return this.isSideLegend
      ? 'flex flex-col gap-2 text-sm shrink-0'
      : 'flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm';
  }

  get legendItems() {
    return this.args.data.map((d, i) => ({
      label: d.category,
      color: this.colors[i % this.colors.length]!,
      value: d.value,
      percent: Math.round((d.value / this.total) * 100),
    }));
  }

  get tooltipData() {
    if (this.hoveredIndex === null) return null;
    const seg = this.segments[this.hoveredIndex];
    if (!seg) return null;
    return {
      category: seg.data.category,
      value: formatNumber(seg.data.value),
      percent: seg.percent,
    };
  }

  get svgMaxWidthStyle() {
    return htmlSafe(`max-width: ${this.size}px;`);
  }

  handleSegmentClick = (segment: ArcSegment) => {
    this.args.onClick?.(segment.data);
  };

  handleSegmentKeydown = (event: KeyboardEvent, segment: ArcSegment) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleSegmentClick(segment);
    }
  };

  setHovered = (index: number) => {
    this.hoveredIndex = index;
  };

  clearHovered = () => {
    this.hoveredIndex = null;
  };

  installObserver = modifier((element: HTMLElement) => {
    const updateSize = (entry?: ResizeObserverEntry) => {
      let width: number;
      let height: number;

      if (entry) {
        ({ width, height } = entry.contentRect);
      } else {
        const rect = element.getBoundingClientRect();
        width = rect.width || element.clientWidth || 300;
        height = rect.height || element.clientHeight || width;
      }

      this.containerWidth = width || 300;
      this.containerHeight = height || this.containerWidth;
    };

    const observer = createDebouncedResizeObserver((entry) => {
      updateSize(entry);
    });
    observer.observe(element);
    updateSize();

    return () => observer.disconnect();
  });

  <template>
    <div data-test-chart data-test-chart-type="donut" class={{this.rootClass}} ...attributes>
      <div class={{this.chartWrapperClass}} {{this.installObserver}}>
        <svg
          role="img"
          aria-label="Donut chart"
          style={{this.svgMaxWidthStyle}}
          viewBox={{this.viewBox}}
          width="100%"
          class="overflow-visible"
        >
          {{#each this.segments as |seg i|}}
            <path
              data-test-chart-segment
              d={{seg.path}}
              fill={{seg.color}}
              class="transition-transform duration-150 origin-center cursor-pointer hover:brightness-110"
              style={{if
                (isEqual this.hoveredIndex i)
                (htmlSafe "transform: scale(1.03); filter: brightness(1.1);")
                (htmlSafe "")
              }}
              role="button"
              tabindex="0"
              aria-label="{{seg.data.category}}: {{seg.data.value}} ({{seg.percent}}%)"
              {{on "mouseenter" (fn this.setHovered i)}}
              {{on "mouseleave" this.clearHovered}}
              {{on "focus" (fn this.setHovered i)}}
              {{on "blur" this.clearHovered}}
              {{on "click" (fn this.handleSegmentClick seg)}}
              {{on "keydown" (fn this.handleSegmentKeydown seg)}}
            />
          {{/each}}

          {{! Center label — show hovered or total }}
          <text
            x={{this.cx}}
            y={{this.cx}}
            text-anchor="middle"
            dominant-baseline="central"
            class="fill-gray-900 dark:fill-gray-100 tio-text-2xl-bold pointer-events-none"
            style={{htmlSafe "font-size: 24px; font-weight: 700;"}}
          >
            {{#if this.tooltipData}}
              {{this.tooltipData.percent}}%
            {{else}}
              {{formatNumber this.total}}
            {{/if}}
          </text>
          <text
            x={{this.cx}}
            y={{add this.cx 20}}
            text-anchor="middle"
            dominant-baseline="central"
            class="fill-gray-700 dark:fill-gray-300 pointer-events-none"
            style={{htmlSafe "font-size: 12px; font-weight: 500;"}}
          >
            {{#if this.tooltipData}}
              {{this.tooltipData.category}}
            {{else}}
              Total
            {{/if}}
          </text>
        </svg>
      </div>

      {{! Legend }}
      {{#if this.showLegend}}
        <div data-test-chart-legend class={{this.legendClass}}>
          {{#each this.legendItems as |item|}}
            <div class="flex items-center gap-1.5">
              <span
                class="inline-block w-3 h-3 rounded-full shrink-0"
                style={{htmlSafe (concat "background-color: " item.color ";")}}
              ></span>
              <span class="tio-text-default font-medium">{{item.label}}</span>
              <span class="tio-text-muted">({{item.percent}}%)</span>
            </div>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}

function isEqual(a: unknown, b: unknown) {
  return a === b;
}

function add(a: number, b: number) {
  return a + b;
}

function concat(...args: (string | number)[]) {
  return args.join('');
}

export type { DonutChartSignature };
